import { motion } from "framer-motion";
import styled, { keyframes } from "styled-components";

/*
 * Korpa, v2 vizuelni retheme na tokene (logika toka kupovine netaknuta):
 * --bg-surface kartice, --border ivice, radius skala, Archivo Black naslov,
 * primary dugme bez gradijenta.
 */

const emptyIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 5rem;
  height: 100%;

  /* prostor za mobilnu sticky traku */
  @media (max-width: 768px) {
    padding-bottom: 7rem;
  }
`;

export const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(40px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: var(--brand);
  }
`;

export const CartLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const CartSection = styled.div`
  background: var(--bg-surface);
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-card);
  padding: 1.5rem;
  gap: 1rem;
  border: 1px solid var(--border);
`;

export const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: var(--brand);
  }
`;

export const CartItem = styled(motion.div)`
  padding: 1.25rem 0.5rem;
  border-bottom: 1px solid var(--border);

  display: grid;
  grid-template-columns: 88px 1fr auto auto;
  gap: 1rem;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  /* Mobilno (v2 s.9.5): slika levo, info desno, qty + cena u redu ispod */
  @media (max-width: 768px) {
    grid-template-columns: 72px 1fr auto;
    grid-template-areas:
      "img info remove"
      "img qty price";
    gap: 0.5rem 0.9rem;
    align-items: start;
  }
`;

export const ItemImage = styled.div`
  width: 88px;
  height: 88px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  font-weight: bold;
  font-size: 0.8rem;
  cursor: zoom-in;
  transition: border-color 0.18s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* na hover veliki preview prati kursor (kao u admin tabeli proizvoda) */
  &:hover {
    border-color: var(--brand);
  }

  @media (max-width: 768px) {
    grid-area: img;
    width: 72px;
    height: 72px;
  }
`;

export const ItemInfo = styled.div`
  width: fit-content;

  @media (max-width: 768px) {
    grid-area: info;
    width: auto;
    min-width: 0;
  }
`;

export const ItemName = styled.h3`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

export const ItemBand = styled.span`
  color: var(--brand);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.3rem;
  display: inline-block;
`;

export const ItemSize = styled.span`
  display: block;
  color: var(--text-secondary);
  font-size: 0.88rem;
`;

/* Kompaktan qty stepper (v2 feedback: bili preveliki) */
export const QuantityControl = styled.div`
  display: inline-flex;
  align-items: center;
  background: transparent;
  border-radius: var(--radius-control);
  border: 1px solid var(--border-strong);
  overflow: hidden;
  height: 30px;

  @media (max-width: 768px) {
    grid-area: qty;
    justify-self: start;
    align-self: center;
  }
`;

export const QuantityBtn = styled.button`
  background: transparent;
  color: var(--text-secondary);
  border: none;
  width: 26px;
  height: 30px;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.06);
    color: var(--text-primary);
  }

  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

export const QuantityInput = styled.input`
  background: transparent;
  color: var(--text-primary);
  border: none;
  height: 30px;
  width: 30px;
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  font-family: var(--font-body);

  &:focus {
    outline: none;
  }
`;

/* Cena iznad qty steppera, poravnato desno uz kolonu cena (v2 feedback) */
export const PriceQtyStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.55rem;

  /* Mobilno: deca postaju direktna grid deca CartItem-a (grid areas) */
  @media (max-width: 768px) {
    display: contents;
  }
`;

export const ItemPrice = styled.div`
  text-align: right;

  @media (max-width: 768px) {
    grid-area: price;
    align-self: center;
  }
`;

export const CurrentPrice = styled.div`
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 700;
`;

export const TotalPrice = styled.div`
  color: var(--text-muted);
  font-size: 0.85rem;
  margin-top: 0.2rem;
`;

/* v2 s.9.2: ghost ikonica, hover --danger + --bg-elevated */
export const RemoveBtn = styled.button`
  background: transparent;
  color: var(--text-secondary);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-control);
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--danger);
    background: var(--bg-elevated);
  }

  @media (max-width: 768px) {
    grid-area: remove;
    justify-self: end;
    width: 32px;
    height: 32px;
  }
`;

/* Cena po komadu ispod line total-a (qty > 1) */
export const PerUnitPrice = styled.div`
  color: var(--text-muted);
  font-size: 13px;
  margin-top: 0.2rem;
`;

/* Header grupe pošiljke, samo kad postoji 2+ grupa (v2 s.9.2) */
export const GroupHeader = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.5rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--border);
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  svg {
    color: var(--brand);
  }
`;

/* Free-shipping modul (v2 s.9.3) */
export const FreeShippingModule = styled.div`
  margin: 1rem 0 0;
`;

export const FreeShippingText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: ${(p) => (p.$reached ? "var(--success)" : "var(--text-secondary)")};
  font-weight: ${(p) => (p.$reached ? 600 : 400)};
  margin-bottom: ${(p) => (p.$reached ? "0" : "0.5rem")};
`;

export const ProgressTrack = styled.div`
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--bg-elevated);
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: ${(p) => Math.min(100, Math.max(0, p.$percent))}%;
  border-radius: var(--radius-pill);
  background: var(--brand);
  transition: width 0.4s ease;
`;

/* Trust red ispod CTA */
export const TrustRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;
  color: var(--text-muted);
  font-size: 12px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }

  svg {
    color: var(--brand);
  }
`;

/* Mobilna sticky traka: Ukupno + CTA (v2 s.9.5) */
export const CartMobileBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1500;
    padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom));
    background: rgba(19, 16, 16, 0.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-top: 1px solid var(--border-strong);

    .total {
      display: flex;
      flex-direction: column;
      line-height: 1.2;

      strong {
        color: var(--text-primary);
        font-size: 1.1rem;
        white-space: nowrap;
      }

      span {
        color: var(--text-muted);
        font-size: 0.68rem;
      }
    }

    button {
      flex: 1;
    }
  }
`;

export const EmptyCart = styled.div`
  text-align: center;
  color: var(--text-secondary);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100dvh - 63px);

  /* sadržaj praznog stanja ulazi stepenasto: ikonica → naslov → tekst/CTA */
  & > * {
    animation: ${emptyIn} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  & > *:nth-child(1) {
    animation-delay: 0.05s;
  }

  & > *:nth-child(2) {
    animation-delay: 0.15s;
  }

  & > *:nth-child(3) {
    animation-delay: 0.25s;
  }

  & > *:nth-child(4) {
    animation-delay: 0.35s;
  }

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }

  svg {
    color: var(--text-muted);
  }

  h3 {
    color: var(--text-primary);
    margin: 1rem 0;
    font-family: var(--font-display);
    text-transform: uppercase;
    letter-spacing: -0.01em;
    font-size: 1.5rem;
  }
  @media (max-width: 768px) {
    min-height: calc(100dvh - 56px);

    svg {
      width: 60px;
    }
  }
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

export const PageBtn = styled.button`
  background: ${({ $active }) =>
    $active ? "var(--brand-dim)" : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--brand)" : "var(--text-secondary)"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(255, 107, 53, 0.45)" : "var(--border-strong)"};
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-control);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.18s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    color: var(--text-primary);
    border-color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const CheckoutSection = styled.div`
  background: var(--bg-surface);
  border-radius: var(--radius-card);
  padding: 1.5rem;
  border: 1px solid var(--border);
  height: fit-content;
  position: sticky;
  top: 90px;

  /* Mobilno: jedna kolona, sticky nema smisla, manji padding */
  @media (max-width: 1024px) {
    position: static;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const OrderSummary = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  color: var(--text-secondary);
  gap: 0.5rem;

  &.total {
    color: var(--text-primary);
    font-weight: bold;
    font-size: 1.1rem;
    border-top: 1px solid var(--border-strong);
    padding-top: 0.8rem;
    margin-top: 1rem;
    margin-bottom: 0;
  }

  .label {
    font-weight: ${({ $bold }) => ($bold ? "bold" : "normal")};
  }

  .value {
    color: ${({ $highlight }) => ($highlight ? "var(--brand)" : "inherit")};
    font-weight: bold;
  }
`;

export const CheckoutForm = styled.form`
  margin-top: 1.5rem;
`;

export const FormSection = styled.div`
  margin-bottom: 1.75rem;
`;

export const FormSectionTitle = styled.h4`
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: var(--brand);
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns || "1fr 1fr"};
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1rem;
  grid-column: ${({ $span }) => $span || "auto"};
`;

export const FormLabel = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 0.45rem;
  font-size: 0.88rem;
`;

export const FormInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: var(--bg-app);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  height: 44px;
  padding: 0 0.9rem;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-family: var(--font-body);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &.error {
    border-color: var(--danger);
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  background: var(--bg-app);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  padding: 0.7rem 0.9rem;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-family: var(--font-body);
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const ErrorMessage = styled.div`
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 0.3rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

export const ShippingOptions = styled.div`
  margin-bottom: 1.5rem;
`;

export const ShippingOption = styled.div`
  background: ${({ $selected }) =>
    $selected ? "var(--brand-dim)" : "rgba(255, 255, 255, 0.02)"};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "rgba(255, 107, 53, 0.45)" : "var(--border-strong)"};
  border-radius: var(--radius-control);
  padding: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    border-color: ${({ $selected }) =>
      $selected ? "var(--brand)" : "var(--text-muted)"};
  }
`;

export const ShippingInfo = styled.div`
  .name {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 0.2rem;
  }
  .desc {
    color: var(--text-secondary);
    font-size: 0.8rem;
  }
`;

export const ShippingPrice = styled.div`
  color: var(--brand);
  font-weight: bold;
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const checkmarkDraw = keyframes`
  0% {
    stroke-dashoffset: 20px;
  }
  100% {
    stroke-dashoffset: -2px;
  }
`;

const spinLoader = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  overflow: hidden;
  transition:
    background 0.18s ease,
    transform 0.18s ease;

  svg {
    transition: transform 0.2s ease;
  }

  &:hover:not(:disabled) {
    background: var(--brand-hover);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    transition-duration: 0.1s;
  }

  /* LOADING STATE */
  &.loading {
    background: var(--bg-elevated);
    color: var(--text-muted);
    cursor: not-allowed;
    pointer-events: none;

    svg {
      animation: ${spinLoader} 1s linear infinite;
    }

    &:hover {
      transform: none;
    }
  }

  /* SUCCESS STATE */
  &.success {
    background: var(--success);
    color: #10240f;
    animation: ${fadeSlideIn} 0.4s ease-out;

    svg {
      stroke-dasharray: 20px;
      stroke-dashoffset: 10px;
      animation: ${checkmarkDraw} 0.5s ease-out 0.2s forwards;
    }
  }

  /* DISABLED STATE */
  &:disabled {
    background: var(--bg-elevated);
    color: var(--text-muted);
    cursor: not-allowed;

    &:hover {
      transform: none;
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.3);
  }

  span {
    transition: all 0.2s ease;
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background 0.2s ease;

    &:hover:not(:disabled) {
      transform: none;
    }

    &:active:not(:disabled) {
      transform: none;
    }

    svg {
      animation: none !important;
    }

    &.success {
      animation: none;
    }
  }
`;
